import React, { useState, useEffect, useCallback } from 'react';
import '../../styles/ExerciseLayout.css';
import KetLetterInput from './KetLetterInput';
import { ketNumberMap } from './ketNumbering';


const KetListenPart2 = ({ exerciseData, attemptId, exerciseId, showHint, isReviewMode, fillHintSignal }) => {
  const [answers, setAnswers] = useState({});
  const [isHydrated, setIsHydrated] = useState(false);

  useEffect(() => {
    if (attemptId && exerciseId) {
      // 1. Prioritize API data in review mode
      if (isReviewMode && exerciseData.questions) {
        const migratedAnswers = {};
        exerciseData.questions.forEach(q => {
          if (q.user_answer && typeof q.user_answer.answer === 'string') {
            migratedAnswers[q.id] = q.user_answer.answer;
          }
        });
        if (Object.keys(migratedAnswers).length > 0) {
          setAnswers(migratedAnswers);
          setIsHydrated(true);
          return;
        }
      }

      // 2. Otherwise load from localStorage
      const attemptKey = `attempt_${attemptId}`;
      const savedAttempt = localStorage.getItem(attemptKey);
      if (savedAttempt) {
        try {
          const allAnswers = JSON.parse(savedAttempt);
          const savedData = allAnswers[exerciseId];
          if (savedData && typeof savedData === 'object') {
            const migratedAnswers = {};
            Object.keys(savedData).forEach(qId => {
              const value = savedData[qId];
              migratedAnswers[qId] = (typeof value === 'object' && value !== null && 'answer' in value) ? value.answer : value;
            });
            setAnswers(migratedAnswers);
          }
        } catch (e) {
          console.error("Failed to parse saved attempt from localStorage", e);
        }
      }
      setIsHydrated(true);
    }
  }, [attemptId, exerciseId, isReviewMode, exerciseData.questions]);

  const saveAnswersToLocalStorage = useCallback((currentAnswers) => {
    if (!isHydrated || !attemptId || !exerciseId || isReviewMode) return;

    const attemptKey = `attempt_${attemptId}`;
    let allAnswers = {};
    try {
      const savedAttempt = localStorage.getItem(attemptKey);
      if (savedAttempt) allAnswers = JSON.parse(savedAttempt) || {};
    } catch (e) {
      allAnswers = {};
    }

    const answersToSave = {};
    Object.keys(currentAnswers).forEach((qId) => {
      answersToSave[qId] = { answer: currentAnswers[qId] };
    });

    allAnswers[exerciseId] = answersToSave;
    localStorage.setItem(attemptKey, JSON.stringify(allAnswers));
  }, [attemptId, exerciseId, isHydrated, isReviewMode]);

  useEffect(() => {
    if (fillHintSignal > 0) {
      const newAnswers = {};
      exerciseData.questions.forEach(q => {
        if (q.accepted_answers && q.accepted_answers.length > 0) {
          newAnswers[q.id] = q.accepted_answers[0];
        }
      });
      setAnswers(newAnswers);
      saveAnswersToLocalStorage(newAnswers);
    }
  }, [fillHintSignal, exerciseData.questions, saveAnswersToLocalStorage]);

  useEffect(() => {
    if (!isHydrated) return;
    const debounceSave = setTimeout(() => {
      saveAnswersToLocalStorage(answers);
    }, 500);
    return () => {
      clearTimeout(debounceSave);
      saveAnswersToLocalStorage(answers);
    };
  }, [answers, isHydrated, saveAnswersToLocalStorage]);

  if (!exerciseData || !exerciseData.content_data || !exerciseData.questions) {
    return <div>Loading exercise data...</div>;
  }

  const { shared_options, context } = exerciseData.content_data;
  const letters = (shared_options || []).map(option => option.id);

  const sortedQuestions = [...exerciseData.questions].sort((a, b) => a.order_index - b.order_index);
  const exampleQuestion = sortedQuestions.find(q => q.points === 0);
  const mainQuestions = sortedQuestions.filter(q => q.points > 0);
  const numberMap = ketNumberMap(exerciseData);

  const handleSelect = (questionId, letter) => {
    setAnswers(prev => ({ ...prev, [questionId]: letter }));
  };

  const ItemRow = ({ question, questionNumber }) => {
    const isExample = question.points === 0;
    const itemName = question.question_text.replace(/\(Example\)/gi, '').trim();

    return (
      <div className={`p-3 rounded-2xl border-2 transition-all flex flex-col sm:flex-row sm:items-center gap-3 ${isExample ? 'bg-blue-50/50 border-blue-100' : 'bg-white border-slate-100 dark:bg-slate-800/50 dark:border-slate-700 hover:border-blue-200 shadow-sm'}`}>
        <div className="flex items-center gap-3 flex-1 min-w-0">
          <span className={`shrink-0 font-black px-2.5 py-1 rounded-xl text-sm border shadow-sm ${isExample ? 'text-white bg-amber-500 border-amber-500' : 'text-slate-400 bg-slate-50 dark:bg-slate-900 border-slate-100 dark:border-slate-800'}`}>
            {isExample ? 'Ví dụ' : questionNumber}
          </span>
          <span className="font-display font-bold text-lg text-slate-700 dark:text-slate-200">{itemName}</span>
        </div>
        <div className="shrink-0 sm:ml-auto">
          <KetLetterInput
            question={question}
            letters={letters}
            answer={answers[question.id]}
            onSelect={handleSelect}
            showHint={showHint}
            isReviewMode={isReviewMode}
            isHydrated={isHydrated}
          />
        </div>
      </div>
    );
  };

  return (
    <div className="w-full h-full p-1 md:px-10 overflow-y-auto custom-scrollbar">
      <div className="max-w-3xl mx-auto w-full py-4 space-y-4">
        {context && (
          <p className="text-center font-bold text-slate-500 dark:text-slate-400 italic m-0">{context}</p>
        )}

        {/* Lettered text options box (A–H) */}
        {shared_options && shared_options.length > 0 && (
          <div className="bg-white dark:bg-slate-800/50 rounded-2xl border-2 border-gray-200 dark:border-slate-600 p-4 shadow-sm">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              {shared_options.map(option => (
                <div key={option.id} className="flex items-center gap-2 px-3 py-2 bg-slate-50 dark:bg-slate-800 rounded-xl border border-slate-100 dark:border-slate-700">
                  <span className="font-display font-black text-amber-600 bg-amber-50 dark:bg-amber-900/30 w-7 h-7 rounded-full flex items-center justify-center border border-gray-200 shrink-0 text-sm">{option.id}</span>
                  <span className="font-bold text-sm text-slate-700 dark:text-slate-200">{option.image_text}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Items to match */}
        <div className="space-y-3">
          {exampleQuestion && (
            <ItemRow question={exampleQuestion} />
          )}
          {mainQuestions.map((q) => (
            <ItemRow
              key={q.id}
              question={q}
              questionNumber={numberMap[q.id]}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default KetListenPart2;
